import { Component, computed, inject, OnDestroy, OnInit } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter, Subject, takeUntil } from 'rxjs';
import { EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { CacheStore, UserStore } from '@/app/lib/store';
import { SpinnerComponent } from '@/app/components/spinner/spinner.component';
import { RouterOutlet } from '@angular/router';

@Component({
  selector: 'dome-root',
  templateUrl: './app.component.html',
  imports: [
    SpinnerComponent,
    RouterOutlet,
  ],
})
export class AppComponent implements OnInit, OnDestroy {
  userStore = inject(UserStore);
  cacheStore = inject(CacheStore);
  authService = inject(MsalService);
  msalBroadcastService = inject(MsalBroadcastService);

  isAuthenticated = computed(() => this.userStore.isAuthenticated());
  private readonly _destroying$ = new Subject<void>();

  ngOnInit(): void {
    this.authService.handleRedirectObservable().subscribe();
    this.authService.instance.enableAccountStorageEvents(); // Optional - This will enable ACCOUNT_ADDED and ACCOUNT_REMOVED events emitted when a user logs in or out of another tab or window

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.ACCOUNT_ADDED ||
            msg.eventType === EventType.ACCOUNT_REMOVED,
        ),
      )
      .subscribe((result: EventMessage) => {
        if (this.authService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.checkAndSetActiveAccount();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntil(this._destroying$),
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      });
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      // add your code for handling multiple accounts here
      this.authService.instance.setActiveAccount(accounts[0]);
      this.userStore.setCurrentUser(accounts[0]);
    } else if (activeAccount && this.userStore.currentUser() == null) {
      this.userStore.setCurrentUser(activeAccount);
    }
  }

  // unsubscribe to events when component is destroyed
  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }
}
